import { Injectable, computed, signal } from '@angular/core';
import {
  AREA_FILTER_MODE,
  AreaSelection,
  IncidentScope,
  MeScope,
  NO_WAREHOUSE_LABEL,
  ScopeWarehouseArea,
  buildDefaultAreaSelection,
} from '../models/scope.model';
import {
  buildAreasParam,
  getMyAreaIds,
  normalizeAreaSelection,
} from '../utilities/scope-params.util';

@Injectable({ providedIn: 'root' })
export class ScopeStateService {
  //* Signals
  private readonly _scope = signal<MeScope | null>(null);
  private readonly _selection = signal<AreaSelection>(buildDefaultAreaSelection());
  private readonly _isLoading = signal<boolean>(false);
  private readonly _isUnavailable = signal<boolean>(false);

  //* Computed
  public readonly scope = this._scope.asReadonly();
  public readonly selection = this._selection.asReadonly();
  public readonly isLoading = this._isLoading.asReadonly();
  public readonly isUnavailable = this._isUnavailable.asReadonly();

  public readonly hasWarehouse = computed<boolean>(() => Boolean(this._scope()?.warehouse));

  public readonly isWithoutWarehouse = computed<boolean>(() => {
    const scope = this._scope();

    return scope !== null && scope.warehouse === null;
  });

  public readonly warehouseAreas = computed<ScopeWarehouseArea[]>(
    () => this._scope()?.warehouseAreas ?? [],
  );

  public readonly defaultLabel = computed<string>(() => {
    const scope = this._scope();

    return scope ? scope.defaultSelection.label || NO_WAREHOUSE_LABEL : '';
  });

  public readonly incidentScope = computed<IncidentScope | null>(
    () => this._scope()?.incidentScope ?? null,
  );

  public readonly areasParam = computed<string | null>(() => buildAreasParam(this._selection()));

  //* Public Methods
  public setScope(scope: MeScope): void {
    this._scope.set(scope);
    this._isUnavailable.set(false);
    if (this._isDefaultWithoutOwnAreas(scope)) this.selectAll();
  }

  public setUnavailable(): void {
    this._scope.set(null);
    this._isUnavailable.set(true);
  }

  public setLoading(value: boolean): void {
    this._isLoading.set(value);
  }

  public selectDefault(): void {
    this._selection.set(buildDefaultAreaSelection());
  }

  public selectAll(): void {
    this._selection.set({ mode: AREA_FILTER_MODE.ALL, areaIds: [] });
  }

  public selectAreas(areaIds: string[]): void {
    this._selection.set(normalizeAreaSelection(areaIds, this._scope()));
  }

  public reset(): void {
    this._scope.set(null);
    this._selection.set(buildDefaultAreaSelection());
    this._isLoading.set(false);
    this._isUnavailable.set(false);
  }

  //* Private Methods
  private _isDefaultWithoutOwnAreas(scope: MeScope): boolean {
    return this._selection().mode === AREA_FILTER_MODE.DEFAULT && getMyAreaIds(scope).length === 0;
  }
}
